import { supabase } from '../lib/supabase';

/**
 * Supabase Friend & Chat Service
 * Uses MongoDB user IDs as primary keys in Supabase tables
 */

// ============================================
// DISCOVERY EXCLUSION (Hybrid approach)
// ============================================

/**
 * Get user IDs to exclude from discovery
 * Returns IDs of users who are already friends or have pending requests
 */
export const getExcludedUserIds = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required', excludedIds: [] };
  }

  try {
    const excludedIds = new Set();

    // Get friends (both directions since it's bidirectional)
    const { data: friends } = await supabase
      .from('friends')
      .select('friend_id')
      .eq('user_id', userId);

    if (friends) {
      friends.forEach(f => excludedIds.add(f.friend_id));
    }

    // Get friend requests sent by current user (any status)
    const { data: sentRequests } = await supabase
      .from('friend_requests')
      .select('receiver_id')
      .eq('sender_id', userId);

    if (sentRequests) {
      sentRequests.forEach(r => excludedIds.add(r.receiver_id));
    }

    // Get friend requests received by current user (any status)
    const { data: receivedRequests } = await supabase
      .from('friend_requests')
      .select('sender_id')
      .eq('receiver_id', userId);

    if (receivedRequests) {
      receivedRequests.forEach(r => excludedIds.add(r.sender_id));
    }

    return { success: true, excludedIds: Array.from(excludedIds) };
  } catch (error) {
    console.error('Error getting excluded user IDs:', error);
    return { success: false, error, excludedIds: [] };
  }
};

// ============================================
// USER MANAGEMENT
// ============================================

/**
 * Ensure user exists in Supabase users table (lazy upsert)
 * Called before any friend/chat operation
 */
export const ensureUserExists = async (userId, name, avatarUrl = null) => {
  if (!userId || !name) {
    return { success: false, error: 'User ID and name are required' };
  }

  try {
    // First check if user exists
    const { data: existingUser } = await supabase
      .from('users')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (existingUser) {
      // Update name/avatar if changed
      const { data, error } = await supabase
        .from('users')
        .update({ name: name, avatar_url: avatarUrl })
        .eq('id', userId)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating user:', error);
      }
      return { success: true, data: data || existingUser };
    }

    // Insert new user
    const { data, error } = await supabase
      .from('users')
      .insert({ id: userId, name: name, avatar_url: avatarUrl })
      .select()
      .single();

    if (error) {
      console.error('Error inserting user:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in ensureUserExists:', error);
    return { success: false, error };
  }
};

// ============================================
// FRIEND REQUEST SYSTEM
// ============================================

/**
 * Send a friend request
 */
export const sendFriendRequest = async (senderId, receiverId) => {
  if (!senderId || !receiverId) {
    return { success: false, error: 'Sender and receiver IDs are required' };
  }

  if (senderId === receiverId) {
    return { success: false, error: 'Cannot send friend request to yourself' };
  }

  try {
    // Check if request already exists from sender to receiver
    const { data: existingRequest1 } = await supabase
      .from('friend_requests')
      .select('id, status')
      .eq('sender_id', senderId)
      .eq('receiver_id', receiverId)
      .maybeSingle();

    if (existingRequest1) {
      if (existingRequest1.status === 'pending') {
        return { success: false, error: 'Friend request already pending' };
      }
      if (existingRequest1.status === 'accepted') {
        return { success: false, error: 'Already friends' };
      }
    }

    // Check if request exists in reverse direction
    const { data: existingRequest2 } = await supabase
      .from('friend_requests')
      .select('id, status')
      .eq('sender_id', receiverId)
      .eq('receiver_id', senderId)
      .maybeSingle();

    if (existingRequest2) {
      if (existingRequest2.status === 'pending') {
        return { success: false, error: 'This user has already sent you a request' };
      }
      if (existingRequest2.status === 'accepted') {
        return { success: false, error: 'Already friends' };
      }
    }

    // Check if already friends
    const { data: existingFriend } = await supabase
      .from('friends')
      .select('id')
      .eq('user_id', senderId)
      .eq('friend_id', receiverId)
      .maybeSingle();

    if (existingFriend) {
      return { success: false, error: 'Already friends' };
    }

    // Insert friend request
    const { data, error } = await supabase
      .from('friend_requests')
      .insert({
        sender_id: senderId,
        receiver_id: receiverId,
        status: 'pending'
      })
      .select()
      .single();

    if (error) {
      console.error('Error sending friend request:', error);
      return { success: false, error: error.message || 'Failed to send friend request' };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendFriendRequest:', error);
    return { success: false, error: error.message || 'Failed to send friend request' };
  }
};

/**
 * Get incoming friend requests for a user
 */
export const getIncomingFriendRequests = async (userId, showAll = false) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    // Get friend requests - optionally show all statuses for debugging
    let query = supabase
      .from('friend_requests')
      .select('id, sender_id, status, created_at')
      .eq('receiver_id', userId)
      .order('created_at', { ascending: false });
    
    // Only filter by pending if not showing all
    if (!showAll) {
      query = query.eq('status', 'pending');
    }
    
    const { data: requests, error } = await query;

    if (error) {
      console.error('Error fetching friend requests:', error);
      return { success: false, error };
    }

    if (!requests || requests.length === 0) {
      return { success: true, data: [] };
    }

    // Get sender info for each request
    const senderIds = requests.map(r => r.sender_id);
    const { data: senders } = await supabase
      .from('users')
      .select('id, name, avatar_url')
      .in('id', senderIds);

    const senderMap = {};
    (senders || []).forEach(s => {
      senderMap[s.id] = s;
    });

    const enrichedRequests = requests.map(r => ({
      ...r,
      sender: senderMap[r.sender_id] || { id: r.sender_id, name: 'Unknown', avatar_url: null }
    }));

    return { success: true, data: enrichedRequests };
  } catch (error) {
    console.error('Error in getIncomingFriendRequests:', error);
    return { success: false, error };
  }
};

/**
 * Accept a friend request
 * - Ensures both users exist in Supabase users table
 * - Updates friend_requests.status to 'accepted'
 * - Inserts 2 rows into friends (bidirectional)
 */
export const acceptFriendRequest = async (requestId, acceptorId, requesterId, acceptorName = 'User', requesterName = 'User') => {
  if (!requestId || !acceptorId || !requesterId) {
    return { success: false, error: 'Request ID, acceptor ID, and requester ID are required' };
  }

  try {
    // IMPORTANT: Ensure both users exist in Supabase users table before creating friendship
    // This prevents foreign key constraint violations
    await ensureUserExists(acceptorId, acceptorName, null);
    await ensureUserExists(requesterId, requesterName, null);

    // Update friend request status
    const { error: updateError } = await supabase
      .from('friend_requests')
      .update({ status: 'accepted' })
      .eq('id', requestId);

    if (updateError) {
      console.error('Error updating friend request:', updateError);
      return { success: false, error: updateError };
    }

    // Insert bidirectional friendship rows
    const { error: insertError } = await supabase
      .from('friends')
      .insert([
        { user_id: acceptorId, friend_id: requesterId },
        { user_id: requesterId, friend_id: acceptorId }
      ]);

    if (insertError) {
      console.error('Error inserting friendship:', insertError);
      return { success: false, error: insertError };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in acceptFriendRequest:', error);
    return { success: false, error };
  }
};

/**
 * Reject a friend request
 */
export const rejectFriendRequest = async (requestId) => {
  if (!requestId) {
    return { success: false, error: 'Request ID is required' };
  }

  try {
    const { error } = await supabase
      .from('friend_requests')
      .update({ status: 'rejected' })
      .eq('id', requestId);

    if (error) {
      console.error('Error rejecting friend request:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in rejectFriendRequest:', error);
    return { success: false, error };
  }
};

// ============================================
// FRIENDS LIST
// ============================================

/**
 * Get friends list for a user
 */
export const getFriendsList = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    // Get friend relationships
    const { data: friendships, error } = await supabase
      .from('friends')
      .select('id, friend_id, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching friends:', error);
      return { success: false, error };
    }

    if (!friendships || friendships.length === 0) {
      return { success: true, data: [] };
    }

    // Get friend info
    const friendIds = friendships.map(f => f.friend_id);
    const { data: friends } = await supabase
      .from('users')
      .select('id, name, avatar_url')
      .in('id', friendIds);

    const friendMap = {};
    (friends || []).forEach(f => {
      friendMap[f.id] = f;
    });

    const enrichedFriendships = friendships.map(f => ({
      ...f,
      friend: friendMap[f.friend_id] || { id: f.friend_id, name: 'Unknown', avatar_url: null }
    }));

    return { success: true, data: enrichedFriendships };
  } catch (error) {
    console.error('Error in getFriendsList:', error);
    return { success: false, error };
  }
};

// ============================================
// CONVERSATIONS
// ============================================

/**
 * Get all conversations for a user (including direct contacts from employers)
 * Returns conversations with the other user's info
 */
export const getAllUserConversations = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required', data: [] };
  }

  try {
    // Get all conversations where user is participant
    const { data: conversations, error } = await supabase
      .from('conversations')
      .select('id, user1_id, user2_id, created_at')
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching conversations:', error);
      return { success: false, error, data: [] };
    }

    if (!conversations || conversations.length === 0) {
      return { success: true, data: [] };
    }

    // Get the other user's ID for each conversation
    const otherUserIds = conversations.map(c => 
      c.user1_id === userId ? c.user2_id : c.user1_id
    );

    // Get user info for all other users
    const { data: users } = await supabase
      .from('users')
      .select('id, name, avatar_url')
      .in('id', otherUserIds);

    const userMap = {};
    (users || []).forEach(u => {
      userMap[u.id] = u;
    });

    // Enrich conversations with other user info
    const enrichedConversations = conversations.map(conv => {
      const otherId = conv.user1_id === userId ? conv.user2_id : conv.user1_id;
      return {
        ...conv,
        other_user: userMap[otherId] || { id: otherId, name: 'Unknown', avatar_url: null }
      };
    });

    return { success: true, data: enrichedConversations };
  } catch (error) {
    console.error('Error in getAllUserConversations:', error);
    return { success: false, error, data: [] };
  }
};

/**
 * Get or create a conversation between two users (lazy creation)
 */
export const getOrCreateConversation = async (user1Id, user2Id) => {
  if (!user1Id || !user2Id) {
    return { success: false, error: 'Both user IDs are required' };
  }

  try {
    // Check if conversation exists (user1 -> user2)
    const { data: conv1 } = await supabase
      .from('conversations')
      .select('id, user1_id, user2_id, created_at')
      .eq('user1_id', user1Id)
      .eq('user2_id', user2Id)
      .maybeSingle();

    if (conv1) {
      return { success: true, data: conv1, isNew: false };
    }

    // Check reverse (user2 -> user1)
    const { data: conv2 } = await supabase
      .from('conversations')
      .select('id, user1_id, user2_id, created_at')
      .eq('user1_id', user2Id)
      .eq('user2_id', user1Id)
      .maybeSingle();

    if (conv2) {
      return { success: true, data: conv2, isNew: false };
    }

    // Create new conversation
    const { data, error } = await supabase
      .from('conversations')
      .insert({
        user1_id: user1Id,
        user2_id: user2Id
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating conversation:', error);
      return { success: false, error };
    }

    return { success: true, data, isNew: true };
  } catch (error) {
    console.error('Error in getOrCreateConversation:', error);
    return { success: false, error };
  }
};

/**
 * Get conversation by ID
 */
export const getConversation = async (conversationId) => {
  if (!conversationId) {
    return { success: false, error: 'Conversation ID is required' };
  }

  try {
    const { data, error } = await supabase
      .from('conversations')
      .select('id, user1_id, user2_id, created_at')
      .eq('id', conversationId)
      .single();

    if (error) {
      console.error('Error fetching conversation:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in getConversation:', error);
    return { success: false, error };
  }
};

// ============================================
// MESSAGES
// ============================================

/**
 * Send a message in a conversation
 */
/**
 * Delete a single message (only the sender can delete their own message)
 */
export const deleteMessage = async (messageId, senderId) => {
  try {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId)
      .eq('sender_id', senderId);
    if (error) return { success: false, error };
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Delete all messages in a conversation
 */
export const deleteAllMessages = async (conversationId) => {
  try {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('conversation_id', conversationId);
    if (error) return { success: false, error };
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const sendMessage = async (conversationId, senderId, message) => {
  if (!conversationId || !senderId || !message) {
    return { success: false, error: 'Conversation ID, sender ID, and message are required' };
  }

  try {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: senderId,
        message: message
      })
      .select()
      .single();

    if (error) {
      console.error('Error sending message:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendMessage:', error);
    return { success: false, error };
  }
};

/**
 * Get messages for a conversation
 */
export const getMessages = async (conversationId, limit = 50) => {
  if (!conversationId) {
    return { success: false, error: 'Conversation ID is required' };
  }

  try {
    // Get messages including file attachments
    const { data: messages, error } = await supabase
      .from('messages')
      .select('id, conversation_id, sender_id, message, created_at, read_at, file_url, file_type, file_name')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
      .limit(limit);

    if (error) {
      console.error('Error fetching messages:', error);
      return { success: false, error };
    }

    if (!messages || messages.length === 0) {
      return { success: true, data: [] };
    }

    // Get sender info
    const senderIds = [...new Set(messages.map(m => m.sender_id))];
    const { data: senders } = await supabase
      .from('users')
      .select('id, name, avatar_url')
      .in('id', senderIds);

    const senderMap = {};
    (senders || []).forEach(s => {
      senderMap[s.id] = s;
    });

    const enrichedMessages = messages.map(m => ({
      ...m,
      sender: senderMap[m.sender_id] || { id: m.sender_id, name: 'Unknown', avatar_url: null }
    }));

    return { success: true, data: enrichedMessages };
  } catch (error) {
    console.error('Error in getMessages:', error);
    return { success: false, error };
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (conversationId, userId) => {
  if (!conversationId || !userId) {
    return { success: false, error: 'Conversation ID and user ID are required' };
  }

  try {
    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('conversation_id', conversationId)
      .neq('sender_id', userId)
      .is('read_at', null);

    if (error) {
      console.error('Error marking messages as read:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in markMessagesAsRead:', error);
    return { success: false, error };
  }
};

// ============================================
// REALTIME SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to new messages in a conversation
 */
export const subscribeToMessages = (conversationId, onInsert, onUpdate) => {
  if (!conversationId) {
    console.error('Conversation ID required for subscription');
    return null;
  }

  const channel = supabase
    .channel(`messages_${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => {
        if (onInsert) onInsert(payload.new);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => {
        if (onUpdate) onUpdate(payload.new);
      }
    )
    .subscribe();

  return channel;
};

/**
 * Subscribe to incoming friend requests
 */
export const subscribeToFriendRequests = (userId, callback) => {
  if (!userId) {
    console.error('User ID required for subscription');
    return null;
  }

  const channel = supabase
    .channel(`friend_requests_${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'friend_requests',
        filter: `receiver_id=eq.${userId}`
      },
      (payload) => {
        callback(payload.new);
      }
    )
    .subscribe();

  return channel;
};

/**
 * Unsubscribe from a channel
 */
export const unsubscribeChannel = (channel) => {
  if (channel) {
    supabase.removeChannel(channel);
  }
};

// ============================================
// BLOCKING USERS
// ============================================

/**
 * Block a user
 */
export const blockUser = async (blockerId, blockedId) => {
  if (!blockerId || !blockedId) {
    return { success: false, error: 'Both user IDs are required' };
  }

  try {
    const { data, error } = await supabase
      .from('blocked_users')
      .insert({
        blocker_id: blockerId,
        blocked_id: blockedId
      })
      .select()
      .single();

    if (error) {
      console.error('Error blocking user:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in blockUser:', error);
    return { success: false, error };
  }
};

/**
 * Unblock a user
 */
export const unblockUser = async (blockerId, blockedId) => {
  if (!blockerId || !blockedId) {
    return { success: false, error: 'Both user IDs are required' };
  }

  try {
    const { error } = await supabase
      .from('blocked_users')
      .delete()
      .eq('blocker_id', blockerId)
      .eq('blocked_id', blockedId);

    if (error) {
      console.error('Error unblocking user:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in unblockUser:', error);
    return { success: false, error };
  }
};

/**
 * Get list of blocked users
 */
export const getBlockedUsers = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    const { data, error } = await supabase
      .from('blocked_users')
      .select('blocked_id, created_at')
      .eq('blocker_id', userId);

    if (error) {
      console.error('Error fetching blocked users:', error);
      return { success: false, error };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error in getBlockedUsers:', error);
    return { success: false, error };
  }
};

/**
 * Check if a user is blocked
 */
export const isUserBlocked = async (blockerId, blockedId) => {
  if (!blockerId || !blockedId) {
    return { success: false, isBlocked: false };
  }

  try {
    const { data } = await supabase
      .from('blocked_users')
      .select('id')
      .eq('blocker_id', blockerId)
      .eq('blocked_id', blockedId)
      .maybeSingle();

    return { success: true, isBlocked: !!data };
  } catch (error) {
    console.error('Error checking blocked status:', error);
    return { success: false, isBlocked: false };
  }
};

// ============================================
// FILE UPLOADS
// ============================================

/**
 * Upload a file to Supabase Storage
 */
export const uploadChatFile = async (file, conversationId) => {
  if (!file || !conversationId) {
    return { success: false, error: 'File and conversation ID are required' };
  }

  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${conversationId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('chat-files')
      .upload(fileName, file);

    if (error) {
      console.error('Error uploading file:', error);
      return { success: false, error };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('chat-files')
      .getPublicUrl(fileName);

    // Determine file type
    let fileType = 'document';
    if (file.type.startsWith('image/')) fileType = 'image';
    else if (file.type.startsWith('video/')) fileType = 'video';
    else if (file.type.startsWith('audio/')) fileType = 'audio';

    return { 
      success: true, 
      data: {
        url: urlData.publicUrl,
        type: fileType,
        name: file.name
      }
    };
  } catch (error) {
    console.error('Error in uploadChatFile:', error);
    return { success: false, error };
  }
};

/**
 * Send a message with file attachment
 */
export const sendMessageWithFile = async (conversationId, senderId, message, fileData) => {
  if (!conversationId || !senderId) {
    return { success: false, error: 'Conversation ID and sender ID are required' };
  }

  try {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: senderId,
        message: message || '',
        file_url: fileData?.url || null,
        file_type: fileData?.type || null,
        file_name: fileData?.name || null
      })
      .select()
      .single();

    if (error) {
      console.error('Error sending message with file:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendMessageWithFile:', error);
    return { success: false, error };
  }
};

/**
 * Get total unread message count for a user across all conversations
 */
export const getUnreadMessageCount = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required', count: 0 };
  }

  try {
    // Get all conversations where this user is a participant
    const { data: conversations, error: convError } = await supabase
      .from('conversations')
      .select('id')
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`);

    if (convError || !conversations || conversations.length === 0) {
      return { success: true, count: 0 };
    }

    const conversationIds = conversations.map(c => c.id);

    // Count unread messages (where sender is NOT the current user and read_at is null)
    const { count, error: msgError } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .in('conversation_id', conversationIds)
      .neq('sender_id', userId)
      .is('read_at', null);

    if (msgError) {
      console.error('Error counting unread messages:', msgError);
      return { success: false, error: msgError, count: 0 };
    }

    return { success: true, count: count || 0 };
  } catch (error) {
    console.error('Error in getUnreadMessageCount:', error);
    return { success: false, error, count: 0 };
  }
};

/**
 * Subscribe to new messages for all user's conversations (for notification bubble)
 */
export const subscribeToAllUserMessages = (userId, onNewMessage) => {
  if (!userId) return null;

  return supabase
    .channel(`user_messages_${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `sender_id=neq.${userId}`
      },
      (payload) => {
        // Callback when a new message is inserted (from someone else)
        if (onNewMessage) onNewMessage(payload.new);
      }
    )
    .subscribe();
};

/**
 * Get unread message counts per friend/conversation
 * Returns a map of friendId -> unreadCount
 */
export const getUnreadCountsPerFriend = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required', counts: {} };
  }

  try {
    // Get all conversations where this user is a participant
    const { data: conversations, error: convError } = await supabase
      .from('conversations')
      .select('id, user1_id, user2_id')
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`);

    if (convError || !conversations || conversations.length === 0) {
      return { success: true, counts: {} };
    }

    const counts = {};

    // For each conversation, count unread messages and map to friend
    for (const conv of conversations) {
      const friendId = conv.user1_id === userId ? conv.user2_id : conv.user1_id;
      
      const { count, error: msgError } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .eq('conversation_id', conv.id)
        .neq('sender_id', userId)
        .is('read_at', null);

      if (!msgError && count > 0) {
        counts[friendId] = count;
      }
    }

    return { success: true, counts };
  } catch (error) {
    console.error('Error in getUnreadCountsPerFriend:', error);
    return { success: false, error, counts: {} };
  }
};

/**
 * Get ALL friend requests for a user (both sent and received, all statuses)
 * Useful for debugging lost requests
 */
export const getAllFriendRequests = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    // Get all requests where user is receiver
    const { data: received, error: receivedError } = await supabase
      .from('friend_requests')
      .select('id, sender_id, receiver_id, status, created_at')
      .eq('receiver_id', userId)
      .order('created_at', { ascending: false });

    // Get all requests where user is sender
    const { data: sent, error: sentError } = await supabase
      .from('friend_requests')
      .select('id, sender_id, receiver_id, status, created_at')
      .eq('sender_id', userId)
      .order('created_at', { ascending: false });

    if (receivedError || sentError) {
      return { success: false, error: receivedError || sentError };
    }

    // Get user info for all involved users
    const allUserIds = [...new Set([
      ...(received || []).map(r => r.sender_id),
      ...(sent || []).map(r => r.receiver_id)
    ])];

    const { data: users } = await supabase
      .from('users')
      .select('id, name, avatar_url')
      .in('id', allUserIds);

    const userMap = {};
    (users || []).forEach(u => {
      userMap[u.id] = u;
    });

    const enrichedReceived = (received || []).map(r => ({
      ...r,
      type: 'received',
      sender: userMap[r.sender_id] || { id: r.sender_id, name: 'Unknown' }
    }));

    const enrichedSent = (sent || []).map(r => ({
      ...r,
      type: 'sent',
      receiver: userMap[r.receiver_id] || { id: r.receiver_id, name: 'Unknown' }
    }));

    return { 
      success: true, 
      data: {
        received: enrichedReceived,
        sent: enrichedSent
      }
    };
  } catch (error) {
    console.error('Error in getAllFriendRequests:', error);
    return { success: false, error };
  }
};

/**
 * Reset a stuck friend request back to pending
 */
export const resetFriendRequest = async (requestId) => {
  try {
    const { error } = await supabase
      .from('friend_requests')
      .update({ status: 'pending' })
      .eq('id', requestId);

    if (error) {
      return { success: false, error };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Subscribe to friends list changes (when someone accepts your request)
 */
export const subscribeToFriendsList = (userId, callback) => {
  if (!userId) return null;

  return supabase
    .channel(`friends_${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'friends',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        if (callback) callback(payload.new);
      }
    )
    .subscribe();
};

export default {
  ensureUserExists,
  sendFriendRequest,
  getIncomingFriendRequests,
  getAllFriendRequests,
  resetFriendRequest,
  acceptFriendRequest,
  rejectFriendRequest,
  getFriendsList,
  getOrCreateConversation,
  getConversation,
  sendMessage,
  getMessages,
  markMessagesAsRead,
  subscribeToMessages,
  subscribeToFriendRequests,
  subscribeToFriendsList,
  unsubscribeChannel,
  blockUser,
  unblockUser,
  getBlockedUsers,
  isUserBlocked,
  uploadChatFile,
  sendMessageWithFile,
  getUnreadMessageCount,
  subscribeToAllUserMessages,
  getUnreadCountsPerFriend
};

// ============================================
// VIDEO/VOICE CALL SIGNALING
// ============================================

/**
 * Initiate a call to another user
 * Creates a call record that the recipient can see and respond to
 */
export const initiateCall = async (callerId, callerName, callerAvatar, receiverId, receiverName, roomId, isVideo = true) => {
  try {
    // First, cancel any existing pending calls from this caller to this receiver
    await supabase
      .from('calls')
      .update({ status: 'cancelled' })
      .eq('caller_id', callerId)
      .eq('receiver_id', receiverId)
      .eq('status', 'pending');

    // Create new call record
    const { data, error } = await supabase
      .from('calls')
      .insert({
        caller_id: callerId,
        caller_name: callerName,
        caller_avatar: callerAvatar,
        receiver_id: receiverId,
        receiver_name: receiverName,
        room_id: roomId,
        is_video: isVideo,
        status: 'pending', // pending, accepted, rejected, cancelled, ended
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, call: data };
  } catch (error) {
    console.error('Error initiating call:', error);
    return { success: false, error };
  }
};

/**
 * Respond to an incoming call (accept or reject)
 */
export const respondToCall = async (callId, response) => {
  try {
    const status = response === 'accept' ? 'accepted' : 'rejected';
    const { data, error } = await supabase
      .from('calls')
      .update({ 
        status,
        responded_at: new Date().toISOString()
      })
      .eq('id', callId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, call: data };
  } catch (error) {
    console.error('Error responding to call:', error);
    return { success: false, error };
  }
};

/**
 * Cancel an outgoing call
 */
export const cancelCall = async (callId) => {
  try {
    const { error } = await supabase
      .from('calls')
      .update({ 
        status: 'cancelled',
        ended_at: new Date().toISOString()
      })
      .eq('id', callId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error cancelling call:', error);
    return { success: false, error };
  }
};

/**
 * End an active call
 */
export const endCall = async (callId) => {
  try {
    const { error } = await supabase
      .from('calls')
      .update({ 
        status: 'ended',
        ended_at: new Date().toISOString()
      })
      .eq('id', callId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error ending call:', error);
    return { success: false, error };
  }
};

/**
 * Subscribe to incoming calls for a user
 */
export const subscribeToIncomingCalls = (userId, onCallReceived) => {
  const channel = supabase
    .channel(`incoming_calls_${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'calls',
        filter: `receiver_id=eq.${userId}`
      },
      (payload) => {
        onCallReceived(payload);
      }
    )
    .subscribe();

  return channel;
};

/**
 * Subscribe to call status changes (for caller to know if call was accepted/rejected)
 */
export const subscribeToCallStatus = (callId, onStatusChange) => {
  const channel = supabase
    .channel(`call_status_${callId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'calls',
        filter: `id=eq.${callId}`
      },
      (payload) => {
        onStatusChange(payload.new);
      }
    )
    .subscribe();

  return channel;
};

/**
 * Get any active/pending call for a user
 */
export const getActiveCall = async (userId) => {
  try {
    // Check for pending incoming calls
    const { data: incomingCall } = await supabase
      .from('calls')
      .select('*')
      .eq('receiver_id', userId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (incomingCall) {
      return { success: true, call: incomingCall, type: 'incoming' };
    }

    // Check for active accepted calls
    const { data: activeCall } = await supabase
      .from('calls')
      .select('*')
      .or(`caller_id.eq.${userId},receiver_id.eq.${userId}`)
      .eq('status', 'accepted')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (activeCall) {
      return { success: true, call: activeCall, type: 'active' };
    }

    return { success: true, call: null };
  } catch (error) {
    // No call found is not an error
    return { success: true, call: null };
  }
};

// ============================================
// DIRECT CONTACT (Privileged Users)
// ============================================

/**
 * Create direct conversation and send initial message
 * For admin/subadmin/employers contacting applicants
 * Bypasses friend request requirement
 * @param contactType - "support" or "job" to categorize the contact
 */
export const createDirectContact = async (senderId, senderName, receiverId, receiverName, initialMessage, contactType = 'job') => {
  if (!senderId || !receiverId) {
    return { success: false, error: 'Both user IDs are required' };
  }

  try {
    // Ensure both users exist in Supabase
    await ensureUserExists(senderId, senderName || 'User', null);
    await ensureUserExists(receiverId, receiverName || 'User', null);

    // Check if receiver has blocked sender
    const blockCheck = await isUserBlocked(receiverId, senderId);
    if (blockCheck.isBlocked) {
      return { success: false, error: 'Unable to contact this user' };
    }

    // Get or create conversation
    const convResult = await getOrCreateConversation(senderId, receiverId);
    if (!convResult.success) {
      return { success: false, error: 'Failed to create conversation' };
    }

    // Store contact type metadata
    try {
      await supabase
        .from('conversation_metadata')
        .upsert({
          conversation_id: convResult.data.id,
          contact_type: contactType,
          initiator_id: senderId
        }, { onConflict: 'conversation_id' });
    } catch (metaError) {
      // If table doesn't exist, create it or ignore
      console.log('Metadata storage skipped:', metaError);
    }

    // Send initial message if provided
    if (initialMessage) {
      await sendMessage(convResult.data.id, senderId, initialMessage);
    }

    return { 
      success: true, 
      conversationId: convResult.data.id,
      isNew: convResult.isNew,
      contactType
    };
  } catch (error) {
    console.error('Error in createDirectContact:', error);
    return { success: false, error };
  }
};

/**
 * Get contact type for a conversation
 */
export const getConversationContactType = async (conversationId) => {
  try {
    const { data } = await supabase
      .from('conversation_metadata')
      .select('contact_type, initiator_id')
      .eq('conversation_id', conversationId)
      .single();
    
    return { success: true, contactType: data?.contact_type || 'unknown', initiatorId: data?.initiator_id };
  } catch {
    return { success: true, contactType: 'unknown', initiatorId: null };
  }
};
